<script setup>
import { computed, ref, onBeforeMount, watch, nextTick } from "vue";
import { Utils } from "@/utils";
import { Validations } from "@/validations";
import { useMainStore } from "@/stores/store";
import InputWrap from "./InputWrap.vue";
import LumenOutputSelector from "./LumenOutputSelector.vue";

const store = useMainStore();

// Size ordering map for consistent dropdown ordering
const SIZE_ORDER = {
	// Standard sizes (most common)
	'Small': 1, 'S': 1,
	'Medium': 2, 'M': 2,
	'Large': 3, 'L': 3,
	'Extra-Large': 4, 'XL': 4,
	'Extra-Large A': 4.1,
	'Extra-Large B': 4.2,
	// Form factors
	'Landscape': 1,
	'Portrait': 2,
	// Single items
	'Sheet': 1,
};

// Props - allow passing a specific product, otherwise use store.currentProduct
const props = defineProps({
	context: {
		type: String,
		default: "StepProduct",
		required: false,
	},
	product: {
		type: Object,
		default: null,
		required: false,
	},
});

// Use prop product if provided, otherwise fall back to store.currentProduct
const currentProduct = computed(() => props.product || store.currentProduct);
$log.log(`[ProductSizeSelector:init] currentProduct: ${JSON.stringify(currentProduct.value, null, 4)}`);
const isStepProduct = computed(() => props.context === "StepProduct");
const isStepReport = computed(() => props.context === "StepReport");

onBeforeMount(() => {
	checkProductSizeVariantError();
});

/*
------------------------------------------------------------------------
Check Product Size Variant Error
This function checks if the user has selected a product size variant and triggers an error message to be displayed if they have not.
------------------------------------------------------------------------
*/
const checkProductSizeVariantError = () => {
	const hasVariants = showOtherVariants();
	const product = currentProduct.value;
	if (hasVariants) {
		if (
			product &&
			product.size_variant === "" &&
			store.user.currentStepName === "Report"
		) {
			store.validation.Product.showErrors = true;
		}
	}
};

const selectedSizeVariants = ref();
const productSizeValid = ref();
productSizeValid.value = Validations.validateProductSize(store);

/*
------------------------------------------------------------------------
Handle Size Change
This function is called when the user selects a product size or variant size.
It updates the currentProduct's metric_sabins value based on the selected size or variant size.
------------------------------------------------------------------------
*/
// Handle size/variant changes from the select dropdowns
//
// RECURSION PREVENTION STRATEGY:
// This function is the PRIMARY handler for size/variant changes. It collects all product updates
// and applies them in a SINGLE batched mutation via updateCurrentProduct(). This is critical for
// preventing recursion when multiple ProductCard components are mounted (2+ products).
//
// The Problem It Solves:
// Without batching, multiple sequential updateCurrentProduct() calls would trigger watchers in
// ALL ProductCard components simultaneously. Each watcher would then call updateExtendedData(),
// which mutates the product again, triggering more watchers → infinite loop.
//
// Prevention mechanisms:
// 1. Active Product Identification: Ensures activeProductId is set before making updates
//    This allows ProductCard watchers to identify which product is being edited and skip updates
//    for other products. Without this, all ProductCard watchers would fire simultaneously.
//
// 2. Batched Updates: Collects ALL updates (size, variant, metric_sabins, dataExtended,
//    selectedLumenOutput) into a single updates object, then applies them in ONE mutation.
//    This reduces reactive updates from N mutations to 1 mutation, dramatically reducing
//    the chance of recursion.
//
// 3. Effective Value Calculation: Uses effectiveSize/effectiveVariant to determine correct
//    values BEFORE mutation, ensuring the correct extended data is found and set in the same
//    mutation cycle.
//
// 4. Extended Data Handling: This function ALWAYS sets dataExtended (either with found data
//    or as empty array []). This allows ProductCard watchers to detect that dataExtended was
//    already handled and skip calling updateExtendedData(), preventing double mutations.
//
// 5. ProductCard Watcher Guards: ProductCard watchers check if dataExtended is already set
//    before calling updateExtendedData(), preventing redundant mutations.
//
// Flow Example (with 2 products):
// - User selects size for Product A
// - handleSizeChange() batches all updates → single mutation
// - ProductCard A watcher fires → sees dataExtended already set → skips updateExtendedData()
// - ProductCard B watcher fires → sees it's not the active product → skips updateExtendedData()
// - No recursion!
const handleSizeChange = (source, newValue) => {
	$log.log(
		`[ProductSizeSelector:handleSizeChange] Called - source: ${source}, newValue: ${newValue}`,
	);

	const hasVariants = showOtherVariants();
	const product = currentProduct.value;
	if (!product) {
		$log.log(`[ProductSizeSelector:handleSizeChange] No current product - aborting`);
		return;
	}

	// Guard against missing product data
	if (!product.data) {
		$log.log(`[ProductSizeSelector:handleSizeChange] Product data not loaded yet - aborting`);
		return;
	}

	$log.log(
		`[ProductSizeSelector:handleSizeChange] Current product ID: ${product.id}, activeProductId: ${store.user.activeProductId}`,
	);

	// RECURSION PREVENTION: Ensure activeProductId is set before making updates
	// This ensures the guard in ProductCard watchers can properly identify which product is being edited
	// Without this, ProductCard watchers might all fire because they can't determine which product is active
	if (!store.user.activeProductId && product.id) {
		$log.log(`[ProductSizeSelector:handleSizeChange] Setting activeProductId to ${product.id}`);
		store.setActiveProduct(product.id);
	}

	// RECURSION PREVENTION: Collect all updates and apply in single mutation
	// This prevents multiple sequential updateCurrentProduct() calls that trigger
	// watchers in all ProductCard components simultaneously when there are 2+ products
	const updates = {};

	// Apply the size/variant change that triggered this handler
	if (source === "primary_size") {
		updates.size = newValue;

		// When size changes, always clear variant - user must reselect
		// Validation will show error state until they do
		if (hasVariants) {
			updates.size_variant = "";
			$log.log(
				`[ProductSizeSelector:handleSizeChange] Size changed - clearing variant selection`,
			);
		}
	} else if (source === "variant_size") {
		updates.size_variant = newValue;
	}

	// Load extended product data based on selection
	let extendedData = null;

	// Determine the effective size and variant for extended data lookup
	// Use the new values from updates if set, otherwise fall back to current product values
	const effectiveSize =
		updates.size !== undefined ? updates.size : product.size;
	const effectiveVariant =
		updates.size_variant !== undefined
			? updates.size_variant
			: product.size_variant;
	// Size change clears variant, so we need to handle extended data lookup differently
	const isClearingVariant = source === "primary_size" && hasVariants;

	// Use variant path only if product HAS variants, a variant is selected, AND we're not clearing it
	if (hasVariants && effectiveVariant && !isClearingVariant) {
		// There is a size variant selected so we use the sabin value from the variant
		const variantData = product.data.sizes.variants.all[effectiveVariant];
		if (variantData) {
			updates.metric_sabins = variantData.metric_sabins;

			// Get the variant info to match against extended data
			const variantInfo = variantData;

			$log.log(`[ProductSizeSelector:handleSizeChange] variantInfo: ${JSON.stringify(variantInfo.l, null, 4)}`);

			// Find ALL extended data entries for the selected product size variant
			// Match on both size and variant label (can have multiple output types)
			// Use effectiveSize (the NEW size) not variantInfo.size (which might be old size)
			if (store.productsExtended && store.productsExtended[product.product]) {
				extendedData = store.productsExtended[product.product].filter(
					(item) => {
						// Match size and variant
						if (item.size !== effectiveSize || item.variant !== variantInfo.l) return false;
						// If product has shapes, also match shape
						if (product.data?.hasShapes && item.shape !== product.shape) return false;
						return true;
					},
				);
			}
		}
	} else {
		// No variant size so use the normal size sabin value
		const sizeData = product.data.sizes.items[effectiveSize];
		if (sizeData) {
			updates.metric_sabins = sizeData.metric_sabins;
		}

		// Find ALL extended data entries for the selected product size (can have multiple output types)
		if (store.productsExtended && store.productsExtended[product.product]) {
			$log.log(
				`[ProductSizeSelector:handleSizeChange] Looking for extended data - Product: ${product.product}, Size: ${effectiveSize}, Variant: (empty)`,
			);
			// Match on size AND empty variant (to avoid matching variant rows)
			extendedData = store.productsExtended[product.product].filter(
				(item) => {
					// Match size and empty variant
					if (item.size !== effectiveSize || item.variant !== "") return false;
					// If product has shapes, also match shape
					if (product.data?.hasShapes && item.shape !== product.shape) return false;
					return true;
				},
			);
		}
	}

	// Apply extended data updates
	// Note: NON-LIT options are added at data import time in data.js
	if (extendedData && extendedData.length > 0) {
		$log.log(
			`[ProductSizeSelector:handleSizeChange] Extended data found for size selection (${extendedData.length} items): ${JSON.stringify(extendedData, null, 4)}`,
		);
		updates.dataExtended = extendedData;
		// Clear lumen output - user must reselect
		// Validation will show error state until they do
		updates.selectedLumenOutput = "";
		$log.log(
			`[ProductSizeSelector:handleSizeChange] Size/variant changed - clearing lumen output selection`,
		);
	} else {
		$log.log(
			`[ProductSizeSelector:handleSizeChange] No extended data found for product ${product.product}, size ${product.size}, variant ${product.size_variant}`,
		);
		// Reset extended data if not found
		updates.dataExtended = [];
		updates.selectedLumenOutput = "";
	}

	// RECURSION PREVENTION: SINGLE mutation instead of multiple sequential calls
	// This batches all updates (size, variant, metric_sabins, dataExtended, selectedLumenOutput)
	// into one update call, preventing multiple reactive updates that would
	// trigger watchers in all ProductCard components simultaneously
	$log.log(
		`[ProductSizeSelector:handleSizeChange] Applying batched updates: ${JSON.stringify(Object.keys(updates))}`,
	);

	// If we have a specific product from props (ProductCard usage), update that specific product
	// Otherwise, use updateCurrentProduct which relies on activeProductId (Product step usage)
	if (props.product && props.product.id) {
		store.updateProduct(props.product.id, updates);
	} else {
		store.updateCurrentProduct(updates);
	}

	$log.log(`[ProductSizeSelector:handleSizeChange] Updates applied successfully`);
};

/*
------------------------------------------------------------------------
Show Other Variants
This function checks if the product has other variants and returns true if it does.
It is used to determine if the other variants should be displayed in the UI.
------------------------------------------------------------------------
*/
const showOtherVariants = () => {
	const product = currentProduct.value;
	if (!product || !product.data?.sizes?.variants?.all) return false;
	const variantsAll = product.data.sizes.variants.all;
	$log.log(`[ProductSizeSelector:showOtherVariants] variantsAll: ${JSON.stringify(variantsAll, null, 4)}`);
	if (
		variantsAll == null ||
		typeof variantsAll !== "object" ||
		Array.isArray(variantsAll)
	)
		return false;
	const hasOtherVariants = Object.keys(variantsAll).length > 0;
	return hasOtherVariants;
};

/*
------------------------------------------------------------------------
Get Option Label
This function returns the label for a product size or variant size.
It is used to display the product size or variant size in the UI.
------------------------------------------------------------------------
*/
const getOptionLabel = (value) => {
	const product = currentProduct.value;
	if (product && product.product === "LN") {
		// For Linea we want the length dimensions to display.
		return `Linea ${value.l} (length: ${value.w}mm)`;
	} else {
		return `${value.l}`;
	}
};

/*
------------------------------------------------------------------------
Filtered Variants Computed Property
This computed property filters the product size variants based on the selected size.
It is used to display the other variants in the UI.
------------------------------------------------------------------------
*/
const filteredVariants = computed(() => {
	const product = currentProduct.value;
	if (!product || !product.size || !product.data?.sizes?.variants?.all)
		return [];
	const result = Object.entries(product.data.sizes.variants.all)
		.filter(([key, value]) => {
			// Match size
			if (value.size !== product.size) return false;
			// If product has shapes, also match shape
			if (product.data?.hasShapes && value.shape !== product.shape) return false;
			return true;
		})
		.map(([key, value]) => ({ key, value }));
	selectedSizeVariants.value = result;
	// $log.log(`result : ${JSON.stringify(result, null, 4)}`);
	return result;
});

/*
------------------------------------------------------------------------
Auto-select single options
When there is only one option in a select list (excluding "Select..."),
automatically select that option.

RECURSION PREVENTION:
- Uses flags to prevent multiple auto-selections
- Only triggers once per product/size combination
- Guards against firing during other product updates
------------------------------------------------------------------------
*/
// Computed: available sizes as sorted array
const availableSizes = computed(() => {
	const product = currentProduct.value;
	if (!product?.data?.sizes?.items) return [];

	return Object.keys(product.data.sizes.items).sort((a, b) => {
		// Check if both are numeric (e.g., Linea sizes: 2, 3, 4, 5, 6, 7, 8)
		const aNum = parseFloat(a);
		const bNum = parseFloat(b);
		if (!isNaN(aNum) && !isNaN(bNum)) {
			return aNum - bNum;
		}

		// Use order map for standard sizes
		const aOrder = SIZE_ORDER[a] ?? 999;
		const bOrder = SIZE_ORDER[b] ?? 999;
		if (aOrder !== bOrder) return aOrder - bOrder;

		// Fallback to alphabetical
		return a.localeCompare(b);
	});
});

// Flags to prevent duplicate auto-selections
const hasAutoSelectedSize = ref(false);
const hasAutoSelectedVariant = ref(false);

// Watch for single size option and auto-select
watch(
	() => [
		availableSizes.value,
		currentProduct.value?.size,
		currentProduct.value?.id,
	],
	([sizes, currentSize, productId]) => {
		// Reset flag if product changes
		if (!productId) {
			hasAutoSelectedSize.value = false;
			return;
		}

		// Skip if already auto-selected for this product
		if (hasAutoSelectedSize.value) return;

		// Only auto-select if there's exactly one size and nothing is selected
		if (sizes.length === 1 && !currentSize) {
			hasAutoSelectedSize.value = true;
			$log.log(
				`[ProductSizeSelector:watch] Product ${productId} auto-selecting single size option: ${sizes[0]}`,
			);
			nextTick(() => {
				handleSizeChange("primary_size", sizes[0]);
			});
		}
	},
	{ immediate: true },
);

// Watch for single variant option and auto-select
watch(
	() => [
		filteredVariants.value,
		currentProduct.value?.size_variant,
		currentProduct.value?.size,
		currentProduct.value?.id,
	],
	([variants, currentVariant, currentSize, productId]) => {
		// Reset variant flag if size changes (new variant options available)
		if (!currentSize) {
			hasAutoSelectedVariant.value = false;
			return;
		}

		// Skip if already auto-selected for this size
		if (hasAutoSelectedVariant.value) return;

		// Only auto-select if there's exactly one variant and nothing is selected
		if (variants.length === 1 && !currentVariant) {
			hasAutoSelectedVariant.value = true;
			$log.log(
				`[ProductSizeSelector:watch] Product ${productId} auto-selecting single variant option: ${variants[0].key}`,
			);
			nextTick(() => {
				handleSizeChange("variant_size", variants[0].key);
			});
		}
	},
	{ immediate: true },
);

// Reset flags when product ID changes (different product selected)
watch(
	() => currentProduct.value?.id,
	() => {
		hasAutoSelectedSize.value = false;
		hasAutoSelectedVariant.value = false;
	},
);

// Reset variant flag when size changes (need to re-evaluate variant options)
watch(
	() => currentProduct.value?.size,
	(newSize, oldSize) => {
		if (newSize !== oldSize) {
			hasAutoSelectedVariant.value = false;
		}
	},
);

/*
------------------------------------------------------------------------
Shape Support
Products like Tessell have shape variants (L, T, X, Y) that the user must
select before choosing size. The shape affects lumen outputs but not
acoustic properties (metric_sabins).
------------------------------------------------------------------------
*/

// Check if current product has shape variants
const hasShapes = computed(() => {
	return currentProduct.value?.data?.hasShapes === true;
});

// Get available shapes for current product
const availableShapes = computed(() => {
	const product = currentProduct.value;
	if (!hasShapes.value || !product?.data?.shapes) return [];
	return Object.entries(product.data.shapes).map(([key, value]) => ({
		key,
		label: value.label || key
	}));
});

// Handle shape selection change
const handleShapeChange = (newShape) => {
	$log.log(`[ProductSizeSelector:handleShapeChange] Shape changed to: ${newShape}`);

	const product = currentProduct.value;
	if (!product) return;

	// Ensure activeProductId is set
	if (!store.user.activeProductId && product.id) {
		store.setActiveProduct(product.id);
	}

	// Shape change clears all dependent selections
	const updates = {
		shape: newShape,
		size: '',
		size_variant: '',
		metric_sabins: '',
		dataExtended: [],
		selectedLumenOutput: '',
	};

	$log.log(`[ProductSizeSelector:handleShapeChange] Clearing dependent selections after shape change`);

	if (props.product && props.product.id) {
		store.updateProduct(props.product.id, updates);
	} else {
		store.updateCurrentProduct(updates);
	}

	// Reset auto-select flags when shape changes
	hasAutoSelectedSize.value = false;
	hasAutoSelectedVariant.value = false;
};

// Flag to prevent duplicate auto-selections for shape
const hasAutoSelectedShape = ref(false);

// Watch for single shape option and auto-select
watch(
	() => [
		availableShapes.value,
		currentProduct.value?.shape,
		currentProduct.value?.id,
	],
	([shapes, currentShape, productId]) => {
		// Reset flag if product changes
		if (!productId) {
			hasAutoSelectedShape.value = false;
			return;
		}

		// Skip if already auto-selected for this product
		if (hasAutoSelectedShape.value) return;

		// Only auto-select if there's exactly one shape and nothing is selected
		if (shapes.length === 1 && !currentShape) {
			hasAutoSelectedShape.value = true;
			$log.log(
				`[ProductSizeSelector:watch] Product ${productId} auto-selecting single shape option: ${shapes[0].key}`,
			);
			nextTick(() => {
				handleShapeChange(shapes[0].key);
			});
		}
	},
	{ immediate: true },
);

// Reset shape flag when product ID changes
watch(
	() => currentProduct.value?.id,
	() => {
		hasAutoSelectedShape.value = false;
	},
);

/*
------------------------------------------------------------------------
Handle Lumen Output Change
Updates the product's selectedLumenOutput in the store.
------------------------------------------------------------------------
*/
const handleLumenOutputChange = (newValue) => {
	const product = currentProduct.value;
	if (!product) return;

	if (props.product && props.product.id) {
		store.updateProduct(props.product.id, { selectedLumenOutput: newValue });
	} else {
		store.updateCurrentProduct({ selectedLumenOutput: newValue });
	}
};
</script>
<template>
	<!-- THESE ONLY SHOW ON THE REPORT STEP -->

	<!-- Shape dropdown (Report Step) - only shown for products with shapes -->
	<InputWrap
		label="Shape"
		:fieldId="`product-shape-${currentProduct?.id || ''}`"
		:required="true"
		v-if="store.stepReport && hasShapes"
		:wrapClass="!currentProduct?.shape ? 'error' : ''"
	>
		<select
			:id="`reportstep-shape-${currentProduct?.id || ''}`"
			name="reportstep-shape"
			:value="currentProduct?.shape || ''"
			@change="(e) => handleShapeChange(e.target.value)"
			class="lx-field-input-wrap lux-focus-ring"
			:class="{ error: !currentProduct?.shape }"
		>
			<option value="" selected>Select...</option>
			<option
				v-for="{ key, label } in availableShapes"
				:key="key"
				:value="key"
			>
				{{ label }}
			</option>
		</select>
	</InputWrap>

	<InputWrap
		label="Size"
		:fieldId="`product-size-${currentProduct?.id || ''}`"
		:required="true"
		v-if="store.stepReport"
		:wrapClass="!currentProduct?.size ? 'error' : ''"
	>
		<select
			:id="`reportstep-size-${currentProduct?.id || ''}`"
			name="reportstep-size"
			:value="currentProduct?.size || ''"
			@change="(e) => handleSizeChange('primary_size', e.target.value)"
			class="lx-field-input-wrap lux-focus-ring"
			:class="{ error: !currentProduct?.size }"
		>
			<option value="" selected>Select...</option>
			<option
				v-for="key in availableSizes"
				:key="key"
				:value="key"
			>
				{{ getOptionLabel(currentProduct?.data?.sizes?.items[key]) }}
			</option>
		</select>
	</InputWrap>

	<InputWrap
		label="Height"
		:fieldId="`height-${currentProduct?.id || ''}`"
		:required="true"
		v-if="store.stepReport && showOtherVariants()"
		:wrapClass="!currentProduct?.size_variant ? 'error' : ''"
	>
		<select
			:id="`reportstep-sizevariant-${currentProduct?.id || ''}`"
			name="reportstep-sizevariant"
			:value="currentProduct?.size_variant || ''"
			@change="(e) => handleSizeChange('variant_size', e.target.value)"
			class="lx-field-input-wrap"
			:class="{ error: !currentProduct?.size_variant }"
		>
			<option value="" selected>Select...</option>
			<option
				v-for="{ key, value } in filteredVariants"
				:key="key"
				:value="key"
			>
				{{ value.l }} (height: {{ value.h }}mm)
			</option>
		</select>
	</InputWrap>

	<!-- THESE ONLY SHOW ON THE PRODUCT STEP -->

	<!-- Shape dropdown (Product Step) - only shown for products with shapes -->
	<InputWrap
		label="Select Product Shape"
		:fieldId="`productstep-shape-${currentProduct?.id || ''}`"
		:showLabel="true"
		:required="true"
		v-if="store.stepProduct && hasShapes"
		:wrapClass="
			store.validation.Product.showErrors && !currentProduct?.shape
				? 'error'
				: ''
		"
	>
		<select
			:id="`productstep-shape-${currentProduct?.id}`"
			name="productstep-shape"
			:value="currentProduct?.shape || ''"
			@change="(e) => handleShapeChange(e.target.value)"
			class="lx-field-input-wrap lux-focus-ring"
			:class="{
				error: store.validation.Product.showErrors && !currentProduct?.shape,
			}"
		>
			<option value="" selected>Select...</option>
			<option
				v-for="{ key, label } in availableShapes"
				:key="key"
				:value="key"
			>
				{{ label }}
			</option>
		</select>
	</InputWrap>

	<InputWrap
		label="Select Product Size"
		:fieldId="`productstep-size-${currentProduct?.id || ''}`"
		:showLabel="true"
		:required="true"
		v-if="store.stepProduct"
		:wrapClass="
			store.validation.Product.showErrors && !currentProduct?.size
				? 'error'
				: ''
		"
	>
		<select
			:id="`productstep-size-${currentProduct?.id}`"
			name="productstep-size"
			:value="currentProduct?.size || ''"
			@change="(e) => handleSizeChange('primary_size', e.target.value)"
			class="lx-field-input-wrap lux-focus-ring"
			:class="{
				error: store.validation.Product.showErrors && !currentProduct?.size,
			}"
		>
			<option value="" selected>Select...</option>
			<option
				v-for="key in availableSizes"
				:key="key"
				:value="key"
			>
				{{ getOptionLabel(currentProduct?.data?.sizes?.items[key]) }}
			</option>
		</select>
	</InputWrap>

	<InputWrap
		label="Size Variant"
		:fieldId="`productstep-sizevariant-${currentProduct?.id || ''}`"
		:showLabel="true"
		:required="true"
		v-if="store.stepProduct && showOtherVariants()"
		:wrapClass="
			store.validation.Product.showErrors && !currentProduct?.size_variant
				? 'error'
				: ''
		"
	>
		<select
			:id="`productstep-sizevariant-${currentProduct?.id || ''}`"
			name="productstep-sizevariant"
			:value="currentProduct?.size_variant || ''"
			@change="(e) => handleSizeChange('variant_size', e.target.value)"
			class="lx-field-input-wrap lux-focus-ring"
			:class="{
				error:
					store.validation.Product.showErrors && !currentProduct?.size_variant,
			}"
		>
			<option value="" selected>Select...</option>
			<option
				v-for="{ key, value } in filteredVariants"
				:key="key"
				:value="key"
			>
				{{ value.l }} (height: {{ value.h }}mm)
			</option>
		</select>
	</InputWrap>

	<!-- Lumen Output Selector (Product Step) -->
	<LumenOutputSelector
		v-if="store.stepProduct"
		:product="currentProduct"
		:selectedValue="currentProduct?.selectedLumenOutput || ''"
		:showErrors="store.validation.Product.showErrors"
		context="StepProduct"
		@update:selectedValue="handleLumenOutputChange"
	/>

	<p
		class="mt-1 text-sm text-red-600"
		id="productsize-error"
		v-if="
			store.stepProduct &&
			store.validateProductSize !== true &&
			store.validation.Product.showErrors
		"
	>
		{{ store.validation.Product.errors.productsize }}
	</p>
</template>
<style scoped></style>
